import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ArrowRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { triageAPI } from "../../services/api";
import { PERFORMANCE_FEE_DISCLAIMER } from "../../helpers/constants";
import {
  DEFAULT_MUNICIPAL_TAX_RATE,
  MO_ASSESSMENT_RATIO,
  calculateSavingsProjection,
} from "./projectedSavingsMath";
import { trackActivity, trackPortalCta } from "../../lib/siteActivity";

const ASSET_MIN = 50_000;
const ASSET_MAX = 5_000_000;
const ASSET_DEFAULT = 250_000;

export const INDUSTRY_OPTIONS = [
  { id: "manufacturing", label: "Manufacturing", overAssessmentVariance: 0.22 },
  { id: "food-beverage", label: "Food & Beverage", overAssessmentVariance: 0.19 },
  { id: "medical", label: "Medical Systems", overAssessmentVariance: 0.21 },
];

const formatCurrency = (value) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const clampAssetValue = (raw) => {
  const parsed = Number(String(raw).replace(/[^\d]/g, ""));
  if (Number.isNaN(parsed)) return ASSET_MIN;
  return Math.min(ASSET_MAX, Math.max(ASSET_MIN, parsed));
};

export default function ProjectedSavingsCalculator() {
  const [industryId, setIndustryId] = useState("manufacturing");
  const [assetValue, setAssetValue] = useState(ASSET_DEFAULT);
  const [hasInteracted, setHasInteracted] = useState(false);

  const industry = INDUSTRY_OPTIONS.find((o) => o.id === industryId) ?? INDUSTRY_OPTIONS[0];

  const { assessmentReduction, cashSavings } = useMemo(
    () =>
      calculateSavingsProjection({
        historicalCost: assetValue,
        taxRate: DEFAULT_MUNICIPAL_TAX_RATE,
        overAssessmentVariance: industry.overAssessmentVariance,
      }),
    [assetValue, industry.overAssessmentVariance]
  );

  useEffect(() => {
    if (!hasInteracted) return;
    const timer = setTimeout(() => {
      void triageAPI.trackCalculator({
        vertical: industry.label,
        assetValue,
        projectedSavings: cashSavings,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [hasInteracted, industry.label, assetValue, cashSavings]);

  const markInteracted = () => {
    if (!hasInteracted) setHasInteracted(true);
  };

  const handleIndustrySelect = (id) => {
    markInteracted();
    setIndustryId(id);
  };

  const handleAssetInput = (event) => {
    markInteracted();
    setAssetValue(clampAssetValue(event.target.value));
  };

  const handleSlider = (event) => {
    markInteracted();
    setAssetValue(Number(event.target.value));
  };

  return (
    <Panel>
      <PanelHeader>
        <Calculator className="h-4 w-4" />
        Projected Savings Calculator
      </PanelHeader>

      <PanelSection>
        <FieldLabel>Industry Vertical</FieldLabel>
        <SegmentGroup role="tablist" aria-label="Industry vertical">
          {INDUSTRY_OPTIONS.map((option) => (
            <SegmentButton
              key={option.id}
              type="button"
              role="tab"
              aria-selected={industryId === option.id}
              $active={industryId === option.id}
              onClick={() => handleIndustrySelect(option.id)}
            >
              {option.label}
            </SegmentButton>
          ))}
        </SegmentGroup>
      </PanelSection>

      <PanelSection>
        <AssetField>
          <FieldLabel htmlFor="asset-value-input">
            Total Cost of Business Equipment / Machinery
          </FieldLabel>
          <AssetInputRow>
            <AssetPrefix>$</AssetPrefix>
            <AssetInput
              id="asset-value-input"
              type="text"
              inputMode="numeric"
              value={assetValue.toLocaleString("en-US")}
              onChange={handleAssetInput}
              aria-valuemin={ASSET_MIN}
              aria-valuemax={ASSET_MAX}
              aria-valuenow={assetValue}
            />
          </AssetInputRow>
          <AssetSlider
            type="range"
            min={ASSET_MIN}
            max={ASSET_MAX}
            step={5_000}
            value={assetValue}
            onChange={handleSlider}
            aria-label="Equipment and machinery value"
          />
          <SliderBounds>
            <span>{formatCurrency(ASSET_MIN)}</span>
            <span>{formatCurrency(ASSET_MAX)}</span>
          </SliderBounds>
        </AssetField>
      </PanelSection>

      <PanelSection>
        <RevealPanel>
          <RevealLabel>Projected Tax Bill Reduction</RevealLabel>
          <RevealValue>{formatCurrency(cashSavings)}</RevealValue>
          <RevealMeta>
            Assessed value reduction {formatCurrency(assessmentReduction)} (
            {Math.round(industry.overAssessmentVariance * 100)}% over-assessment corrected) ·{" "}
            {Math.round(MO_ASSESSMENT_RATIO * 10000) / 100}% MO assessed ratio ·{" "}
            {Math.round(DEFAULT_MUNICIPAL_TAX_RATE * 100)}% local levy
          </RevealMeta>
          <RevealNote>{PERFORMANCE_FEE_DISCLAIMER}</RevealNote>
        </RevealPanel>
      </PanelSection>

      <PanelFooter>
        <CalculatorCta
          to="/?triggerSignup=true"
          onClick={() => {
            trackActivity({
              event: "calculator_cta",
              path: "/",
              label: industry.label,
              meta: { assetValue, projectedSavings: cashSavings },
            });
            trackPortalCta("calculator-cta");
          }}
        >
          Check My Equipment Assessment
          <ArrowRight className="h-4 w-4" />
        </CalculatorCta>
      </PanelFooter>
    </Panel>
  );
}

const Panel = styled.div`
  border: 1px solid #1e293b;
  background: #0b1220;
  box-shadow: 0 24px 48px rgba(2, 6, 23, 0.35);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #1e293b;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const PanelSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #1e293b;
`;

const PanelFooter = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 10px;
`;

const SegmentGroup = styled.div`
  display: flex;
  width: 100%;
  border: 1px solid #1e293b;
  background: #0f172a;
`;

const SegmentButton = styled.button`
  flex: 1;
  min-width: 0;
  padding: 12px 10px;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: none;
  border-right: 1px solid #1e293b;
  border-bottom: 3px solid ${(p) => (p.$active ? "#22d3ee" : "transparent")};
  background: ${(p) => (p.$active ? "#0f172a" : "#0b1220")};
  color: ${(p) => (p.$active ? "#67e8f9" : "#64748b")};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  &:last-child {
    border-right: none;
  }

  &:hover {
    color: ${(p) => (p.$active ? "#67e8f9" : "#cbd5e1")};
    background: ${(p) => (p.$active ? "#0f172a" : "#111827")};
  }
`;

const AssetField = styled.div``;

const AssetInputRow = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #1e293b;
  background: #0f172a;
`;

const AssetPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 14px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.9375rem;
  color: #64748b;
  border-right: 1px solid #1e293b;
`;

const AssetInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border: none;
  background: transparent;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 1rem;
  font-weight: 600;
  color: #f1f5f9;
  font-variant-numeric: tabular-nums;

  &:focus {
    outline: none;
  }
`;

const AssetSlider = styled.input`
  width: 100%;
  margin-top: 14px;
  accent-color: #22d3ee;
  cursor: pointer;
`;

const SliderBounds = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 0.625rem;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  color: #475569;
`;

const RevealPanel = styled.div`
  border: 1px solid #1e293b;
  background: #0f172a;
  padding: 18px 16px;
`;

const RevealLabel = styled.p`
  margin: 0 0 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const RevealValue = styled.p`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #22d3ee;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  text-shadow: 0 0 24px rgba(34, 211, 238, 0.2);

  @media (min-width: 1024px) {
    font-size: 2.25rem;
  }
`;

const RevealMeta = styled.p`
  margin: 10px 0 0;
  font-size: 0.6875rem;
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
`;

const RevealNote = styled.p`
  margin: 14px 0 0;
  font-size: 0.75rem;
  line-height: 1.55;
  color: #94a3b8;
  border-top: 1px solid #1e293b;
  padding-top: 14px;
  white-space: pre-line;
`;

const CalculatorCta = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #1e293b;
  background: transparent;
  color: #22d3ee;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  &:hover {
    background: rgba(34, 211, 238, 0.1);
    border-color: #22d3ee;
    color: #67e8f9;
  }
`;
